import { useState, useMemo } from 'react'
import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useResidents } from '@/hooks/use-supabase-data'
import { Users, Search } from 'lucide-react'
import { CommunityCard } from '@/components/community-card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { Resident } from '@shared/schema'

export default function Community() {
  const { residents, loading } = useResidents()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')

  // All unique categories (unfiltered) for the filter chips
  const allCategories = useMemo(() => {
    if (!residents) return []
    const cats = Array.from(new Set(residents.map((r) => r.category || 'Other'))).sort((a, b) => {
      if (a === 'Other') return 1
      if (b === 'Other') return -1
      return a.localeCompare(b)
    })
    return cats
  }, [residents])

  // Group residents by category, filtered by search query and selected category
  const residentsByCategory = useMemo(() => {
    if (!residents) return {}

    const q = searchQuery.toLowerCase().trim()
    const filtered = residents.filter((r) => {
      const matchesSearch =
        !q ||
        r.name.toLowerCase().includes(q) ||
        (r.role && r.role.toLowerCase().includes(q))
      const matchesCategory =
        selectedCategory === 'All' || (r.category || 'Other') === selectedCategory
      return matchesSearch && matchesCategory
    })

    const grouped: Record<string, Resident[]> = {}
    filtered.forEach((resident) => {
      const category = resident.category || 'Other'
      if (!grouped[category]) grouped[category] = []
      grouped[category].push(resident)
    })

    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Other') return 1
      if (b === 'Other') return -1
      return a.localeCompare(b)
    })

    const sorted: Record<string, Resident[]> = {}
    sortedCategories.forEach((cat) => { sorted[cat] = grouped[cat] })
    return sorted
  }, [residents, searchQuery, selectedCategory])

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-slate-600">Loading community members...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!residents || residents.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-poppins text-2xl font-bold text-foreground mb-2">
              Community Members
            </h2>
            <p className="font-inter text-muted-foreground">
              No community members added yet.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  const filteredCategories = Object.keys(residentsByCategory)
  const totalFiltered = Object.values(residentsByCategory).reduce((sum, arr) => sum + arr.length, 0)
  const isFiltering = searchQuery.trim() !== '' || selectedCategory !== 'All'

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="text-center mb-6">
            <h1 className="font-poppins text-4xl md:text-5xl font-bold text-foreground mb-2">
              Our Community
            </h1>
            <p className="font-inter text-muted-foreground text-lg">
              Meet the dedicated members serving our village!
            </p>
          </div>

          {/* Search bar */}
          <div className="relative max-w-lg mx-auto mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search by name or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Category filter chips */}
          {allCategories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                variant={selectedCategory === 'All' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('All')}
                className="rounded-full"
              >
                All
              </Button>
              {allCategories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className="rounded-full"
                >
                  {cat}
                </Button>
              ))}
            </div>
          )}
        </div>

        {isFiltering && (
          <p className="font-inter text-sm text-muted-foreground mb-6 text-center">
            {totalFiltered} member{totalFiltered !== 1 ? 's' : ''} found
          </p>
        )}

        {filteredCategories.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="font-inter text-muted-foreground text-lg">No members match your search.</p>
            <Button
              variant="ghost"
              onClick={() => { setSearchQuery(''); setSelectedCategory('All') }}
              className="mt-2"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          filteredCategories.map((category) => (
            <CategorySection
              key={category}
              category={category}
              residents={residentsByCategory[category]}
            />
          ))
        )}
      </main>
      <Footer />
    </div>
  )
}

interface CategorySectionProps {
  category: string
  residents: Resident[]
}

function CategorySection({ category, residents }: CategorySectionProps) {
  const initials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 neo-icon-box">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="font-poppins text-2xl md:text-3xl font-bold text-foreground">
            {category}
          </h2>
          <p className="font-inter text-sm text-muted-foreground">
            {residents.length} member{residents.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
        {residents.map((resident) => (
          <CommunityCard key={resident.id} resident={resident} />
        ))}
      </div>
    </section>
  )
}
